import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTogglePath, getLangFromPath, lp } from '../../utils/routes';

const navLinks = [
  { key: 'nav_how_it_works', href: '/how-it-works' },
  { key: 'nav_plans', href: '/plans' },
  { key: 'nav_industries', href: '/industries' },
  { key: 'nav_start_llc', href: '/start-your-llc' },
  { key: 'nav_about', href: '/about' },
  { key: 'nav_blog', href: '/blog' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Derive language from URL — always reliable, no async delay
  const lang = getLangFromPath(location.pathname);
  const isEs = lang === 'es';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    const target = getTogglePath(location.pathname);
    navigate(target);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E9D9F2] transition-all duration-300 ${
          scrolled ? 'shadow-sm' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            {/* Logo */}
            <Link to={lp('/', lang)} className="flex items-center cursor-pointer whitespace-nowrap">
              <img
                src={`${import.meta.env.BASE_URL}KAILAC.png`}
                alt="Kailac"
                className="h-10 sm:h-11 w-auto max-w-[140px] object-contain object-left"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={lp(link.href, lang)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-[#F8F3FB] transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>

            {/* Desktop Right: Lang + CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                aria-label={isEs ? t('nav_lang_switch_to_en') : t('nav_lang_switch_to_es')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-[#E9D9F2] text-gray-600 hover:border-[#4F2D63] hover:text-[#4F2D63] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-sm"></i>
                <span className="font-bold text-[#4F2D63]">{isEs ? 'ES' : 'EN'}</span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-400">{isEs ? 'EN' : 'ES'}</span>
              </button>

              <Link
                to={lp('/contact', lang)}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-[#E9D9F2] text-gray-700 hover:border-[#4F2D63] hover:text-[#4F2D63] transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav_book_call')}
              </Link>

              <Link
                to={lp('/contact', lang)}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#4F2D63] hover:bg-[#3E234F] text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav_apply')}
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors text-gray-700 hover:bg-[#F8F3FB]"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-[#E9D9F2] px-6 pb-6">
            <nav className="flex flex-col gap-1 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={lp(link.href, lang)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-[#F8F3FB] cursor-pointer whitespace-nowrap"
                >
                  {t(link.key)}
                </Link>
              ))}
            </nav>
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-[#E9D9F2]">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-gray-600 hover:bg-[#F8F3FB] cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line"></i>
                <span>{isEs ? t('nav_lang_switch_to_en') : t('nav_lang_switch_to_es')}</span>
              </button>
              <Link
                to={lp('/contact', lang)}
                className="w-full py-3 rounded-lg text-sm font-medium text-center border border-[#E9D9F2] text-gray-700 hover:bg-[#F8F3FB] cursor-pointer whitespace-nowrap"
              >
                {t('nav_book_call')}
              </Link>
              <Link
                to={lp('/contact', lang)}
                className="w-full py-3 rounded-lg text-sm font-semibold text-center bg-[#4F2D63] hover:bg-[#3E234F] text-white cursor-pointer whitespace-nowrap"
              >
                {t('nav_apply')}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Sticky CTA Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#4F2D63] px-4 py-3 flex items-center gap-3">
        <Link
          to={lp('/contact', lang)}
          className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-center text-white border border-[#5E3B72] hover:bg-[#3E234F] cursor-pointer whitespace-nowrap"
        >
          {t('nav_book_call')}
        </Link>
        <Link
          to={lp('/contact', lang)}
          className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-center bg-white text-[#4F2D63] hover:bg-[#F8F3FB] cursor-pointer whitespace-nowrap"
        >
          {t('nav_apply')}
        </Link>
      </div>
    </>
  );
}
